import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserCanCreatePurchaseForConsignee } from 'src/hooks/useUserIsConsignorMasterUI';
import { SellerAccountClient } from 'src/WebApiController';

export const GET_CONSIGNEE_SELLER_USERS_QUERY_KEY =
  'SellerAccountClient.getSellerUsersForSellerAccount.consignee';

/**
 * Users of a single consignee account, e.g. for "Purchased By" when creating a purchase for that consignee.
 * Returns nothing when no consignee is given.
 */
export const useGetConsigneeSellerUsersQuery = (
  consigneeSellerAccountId: string | null
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const canCreatePurchaseForConsignee = useUserCanCreatePurchaseForConsignee();

  const shouldQuery =
    canCreatePurchaseForConsignee &&
    consigneeSellerAccountId != null &&
    activeAccountWebClientConfig.activeAccountId != null;

  return useQuery({
    queryKey: [
      GET_CONSIGNEE_SELLER_USERS_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
      consigneeSellerAccountId,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getSellerUsersForSellerAccount(consigneeSellerAccountId);
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError(
          'SellerAccountClient.getSellerUsersForSellerAccount',
          error,
          {
            consigneeSellerAccountId,
          }
        );
      },
    },
  });
};
